import { useEffect, useState, useCallback } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { MemoryCard } from '@/components/admin/cards/MemoryCard';
import { clientsService } from '@/services/admin/clientsService';
import type { ClientDetail } from '@/types/admin';
import { formatDate } from '@/lib/date-utils';
import { Loader2, User, Mail, Calendar } from 'lucide-react';

interface ClientDrawerProps {
  userId: string | null;
  open: boolean;
  onClose: () => void;
  onCompanionClick?: (userId: string) => void;
}

/**
 * ClientDrawer - Side panel showing complete client detail
 */
export function ClientDrawer({
  userId,
  open,
  onClose,
  onCompanionClick,
}: ClientDrawerProps) {
  const [clientDetail, setClientDetail] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchClientDetail = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await clientsService.fetchClientDetail(userId);
      setClientDetail(data);
    } catch {
      setError('Error al cargar los detalles del cliente');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open && userId) {
      fetchClientDetail();
    } else {
      // Reset state when closed
      setClientDetail(null);
      setError(null);
    }
  }, [open, userId, fetchClientDetail]);

  return (
    <Sheet open={open} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <p className="text-red-600">{error}</p>
            <button
              onClick={fetchClientDetail}
              className="text-sm text-blue-600 hover:underline"
            >
              Reintentar
            </button>
          </div>
        ) : clientDetail ? (
          <>
            <SheetHeader>
              <SheetTitle>Detalle del cliente</SheetTitle>
              <SheetDescription>
                Historial completo de memorias y reacciones
              </SheetDescription>
            </SheetHeader>

            <div className="mt-6 space-y-6">
              {/* Client info */}
              <div className="space-y-3 rounded-lg bg-gray-50 p-4">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="h-4 w-4" />
                  <span className="font-medium">{clientDetail.client.email}</span>
                </div>
                {clientDetail.client.name && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="h-4 w-4" />
                    <span>{clientDetail.client.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>Cliente desde {formatDate(clientDetail.client.created_at)}</span>
                </div>
              </div>

              {/* Memories */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Memorias ({clientDetail.memories.length})
                </h3>
                {clientDetail.memories.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    Este cliente no tiene memorias todavía
                  </p>
                ) : (
                  <div className="space-y-4">
                    {clientDetail.memories.map((memory) => (
                      <MemoryCard
                        key={memory.id}
                        memory={memory}
                        onCompanionClick={onCompanionClick}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
